import {
  circle,
  ellipse,
  fill,
  flat,
  line,
  P,
  poly,
  polyline,
  rect,
  shine,
  type IconDef,
  type Shape,
} from '../kit';

const r2 = (v: number): number => Math.round(v * 100) / 100;

/** The Camaro's wheels: black tires with silver hubs, centered at each x on the axle line y. */
const wheels = (xs: number[], y: number, r = 3.4, hub = 1.4): Shape[] => [
  fill(P.black, xs.map((x) => circle(x, y, r)).join('')),
  flat(P.silver, xs.map((x) => circle(x, y, hub)).join('')),
];

/** A knobby mud tire: `n` lugs around a circle (drawn without an outline, so the lugs stay crisp). */
function knobby(cx: number, cy: number, r: number, n = 12, depth = 0.9): string {
  const pts: number[] = [];
  const step = (Math.PI * 2) / n;
  for (let i = 0; i < n; i++) {
    for (const [t, rr] of [
      [0, r - depth],
      [0.1, r],
      [0.45, r],
      [0.55, r - depth],
    ] as const) {
      const a = (i + t) * step;
      pts.push(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
    }
  }
  return poly(...pts);
}

/** A smooth closed curve through the points (x0, y0, x1, y1, ...): splats and blobs. */
function blob(...p: number[]): string {
  const k = p.length / 2;
  const at = (i: number): [number, number] => {
    const j = (i + k) % k;
    return [p[2 * j] as number, p[2 * j + 1] as number];
  };
  let d = `M${r2(p[0] as number)} ${r2(p[1] as number)}`;
  for (let i = 0; i < k; i++) {
    const [ax, ay] = at(i - 1);
    const [bx, by] = at(i);
    const [cx, cy] = at(i + 1);
    const [dx, dy] = at(i + 2);
    d +=
      `C${r2(bx + (cx - ax) / 6)} ${r2(by + (cy - ay) / 6)} ` +
      `${r2(cx - (dx - bx) / 6)} ${r2(cy - (dy - by) / 6)} ${r2(cx)} ${r2(cy)}`;
  }
  return `${d}Z`;
}

/** A star with `k` points. */
function star(cx: number, cy: number, ro: number, ri: number, k = 5): string {
  const pts: number[] = [];
  for (let i = 0; i < 2 * k; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / k;
    const rr = i % 2 ? ri : ro;
    pts.push(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
  }
  return poly(...pts);
}

/** A flower head of `k` round petals, `r` to the petal tips. */
function flower(cx: number, cy: number, r: number, k = 5): string {
  const j = r * 0.66;
  const pr = r2(r * 0.395);
  const pt = (i: number): string => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / k;
    return `${r2(cx + Math.cos(a) * j)} ${r2(cy + Math.sin(a) * j)}`;
  };
  let d = `M${pt(0)}`;
  for (let i = 1; i <= k; i++) d += `A${pr} ${pr} 0 1 1 ${pt(i)}`;
  return `${d}Z`;
}

/** A body of revolution seen side-on: half-width `w(y)` from `y0` down to `y1`, sampled. */
function profile(cx: number, y0: number, y1: number, w: (y: number) => number, steps = 24): string {
  const right: number[] = [];
  const left: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const y = y0 + ((y1 - y0) * i) / steps;
    right.push(cx + w(y), y);
    left.unshift(cx - w(y), y);
  }
  return poly(...right, ...left);
}

/** The racing flag: a waving cloth with its checks bent along with it, on a pole. */
function checkeredFlag(): Shape[] {
  const x0 = 7.4;
  const x1 = 28.8;
  const y0 = 5.6;
  const h = 13.4;
  const cols = 5;
  const rows = 4;
  const wave = (x: number): number => {
    const t = (x - x0) / (x1 - x0);
    return Math.sin(t * Math.PI * 2.2) * (0.3 + 1.3 * t);
  };
  const edge = (xa: number, xb: number, v: number): number[] => {
    const out: number[] = [];
    const n = Math.max(2, Math.ceil(Math.abs(xb - xa) / 0.7));
    for (let i = 0; i <= n; i++) {
      const x = xa + ((xb - xa) * i) / n;
      out.push(x, y0 + v * h + wave(x));
    }
    return out;
  };
  const cloth = poly(...edge(x0, x1, 0), ...edge(x1, x0, 1));
  let checks = '';
  const cw = (x1 - x0) / cols;
  for (let i = 0; i < cols; i++)
    for (let j = 0; j < rows; j++)
      if ((i + j) % 2 === 0) {
        const xa = x0 + i * cw;
        checks += poly(...edge(xa, xa + cw, j / rows), ...edge(xa + cw, xa, (j + 1) / rows));
      }
  return [
    flat(P.white, cloth),
    flat(P.black, checks),
    line(cloth, 1.6),
    fill(P.wood, rect(5.2, 4.6, 2.4, 24.4, 1.2)),
    fill(P.gold, circle(6.4, 4.4, 1.6)),
  ];
}

/**
 * The big rig in a simple oblique 3/4 view (x across the front, y up, z back along the side):
 * flat faces, light interior edges, one outline around the whole cab.
 */
function semiTruck(): Shape[] {
  const p = (x: number, y: number, z: number): [number, number] => [
    5.7 + 1.2 * (x + 0.55 * z),
    28.9 - 1.2 * (y + 0.3 * z),
  ];
  const pts = (...q: [number, number, number][]): number[] => q.flatMap(([x, y, z]) => p(x, y, z));
  const quad = (...q: [number, number, number][]): string => poly(...pts(...q));
  const outline = poly(
    ...pts(
      [0, 2, 0],
      [0, 8.6, 0],
      [0, 8.6, 5.5],
      [0, 16, 5.5],
      [0, 16, 13],
      [10, 16, 13],
      [10, 2, 13],
      [10, 2, 0],
    ),
  );
  const side = quad([10, 2, 0], [10, 8.6, 0], [10, 8.6, 5.5], [10, 16, 5.5], [10, 16, 13], [10, 2, 13]);
  const tops =
    quad([0, 8.6, 0], [10, 8.6, 0], [10, 8.6, 5.5], [0, 8.6, 5.5]) +
    quad([0, 16, 5.5], [10, 16, 5.5], [10, 16, 13], [0, 16, 13]);
  const edges =
    polyline(...pts([0, 8.6, 0], [10, 8.6, 0], [10, 2, 0])) +
    polyline(...pts([10, 8.6, 0], [10, 8.6, 5.5], [0, 8.6, 5.5])) +
    polyline(...pts([10, 8.6, 5.5], [10, 16, 5.5], [0, 16, 5.5])) +
    polyline(...pts([10, 16, 5.5], [10, 16, 13]));
  const [fx, fy] = p(10, 2.4, 2.6);
  const [d1x, d1y] = p(10, 2.4, 10.4);
  const [d2x, d2y] = p(10, 2.4, 12.4);
  const [sx, sy] = p(10.6, 18.5, 9.8);
  const [gx, gy] = p(2, 8, 0);
  return [
    fill(P.black, ellipse(d2x, d2y, 1.7, 2.9) + ellipse(d1x, d1y, 1.7, 2.9)),
    flat(P.red, outline),
    flat(P.black, side, 0.14),
    flat(P.white, tops, 0.18),
    flat(
      P.sky,
      quad([10, 10.4, 6.8], [10, 14.4, 6.8], [10, 14.4, 9.8], [10, 10.4, 9.8]) +
        quad([0.8, 10.2, 5.5], [9.2, 10.2, 5.5], [9.2, 15, 5.5], [0.8, 15, 5.5]),
    ),
    line(polyline(...p(5, 10.4, 5.5), ...p(5, 14.8, 5.5)), 1.2),
    line(edges, 1.2),
    line(outline, 1.6),
    fill(P.silver, rect(sx - 1, sy, 2, 16.2, 1)),
    fill(P.black, ellipse(fx, fy, 1.7, 2.9)),
    flat(P.silver, ellipse(fx, fy, 0.7, 1.2)),
    fill(P.silver, rect(gx, gy, 7.2, 6.2, 0.8), { w: 1.2 }),
    line(
      `M${gx + 1.8} ${gy + 1.3}v3.6M${gx + 3.6} ${gy + 1.3}v3.6M${gx + 5.4} ${gy + 1.3}v3.6`,
      1.2,
      P.steel,
    ),
    fill(P.silver, rect(5, 25.9, 13, 2.6, 1)),
  ];
}

/**
 * The mud splat: a lumpy blob with splash arms, drips and droplets, outlined as one shape
 * (everything is drawn once in ink with the outline, then again in mud on top).
 */
function mudSplat(): Shape[] {
  const cx = 16;
  const cy = 14.4;
  // counter-clockwise (on screen) polygons, so overlapping parts never cancel out
  const ccw = (pts: number[]): number[] => {
    let a = 0;
    for (let i = 0; i < pts.length; i += 2) {
      const j = (i + 2) % pts.length;
      a += (pts[i] as number) * (pts[j + 1] as number) - (pts[j] as number) * (pts[i + 1] as number);
    }
    if (a < 0) return pts;
    const out: number[] = [];
    for (let i = pts.length - 2; i >= 0; i -= 2) out.push(pts[i] as number, pts[i + 1] as number);
    return out;
  };
  const radii = [7.4, 7.8, 7, 7.6, 6.8, 7.4, 8, 7, 7.6, 6.8];
  const ring: number[] = [];
  radii.forEach((r, i) => {
    const a = (i / radii.length) * Math.PI * 2;
    ring.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  });
  const parts: string[] = [blob(...ccw(ring))];
  /** A splash arm from the middle out to a round droplet: angle (deg), reach, base half-width, tip radius. */
  const arm = (deg: number, reach: number, base: number, tip: number): void => {
    const a = (deg * Math.PI) / 180;
    const [dx, dy, nx, ny] = [Math.cos(a), Math.sin(a), -Math.sin(a), Math.cos(a)];
    const tx = cx + dx * reach;
    const ty = cy + dy * reach;
    const k = tip * 0.8;
    parts.push(
      poly(
        ...ccw([
          cx + nx * base,
          cy + ny * base,
          tx + nx * k,
          ty + ny * k,
          tx - nx * k,
          ty - ny * k,
          cx - nx * base,
          cy - ny * base,
        ]),
      ),
    );
    parts.push(circle(tx, ty, tip));
  };
  arm(-124, 10.2, 3, 1.9);
  arm(-62, 10.6, 2.6, 1.8);
  arm(-14, 10.4, 3, 1.7);
  arm(28, 9.2, 2.6, 1.5);
  arm(152, 10.4, 2.8, 1.7);
  arm(206, 8.8, 2.4, 1.4);
  /** A drip running straight down from the bottom of the blob. */
  const drip = (x: number, y1: number, r: number): void => {
    parts.push(poly(...ccw([x - 1.1, cy, x - 0.8, y1, x + 0.8, y1, x + 1.1, cy])));
    parts.push(circle(x, y1, r));
  };
  drip(12.6, 26.8, 1.7);
  drip(18.6, 24.6, 1.4);
  const flecks = [circle(26.6, 25.8, 1.6), circle(4.8, 4.8, 1.5), circle(5.2, 25, 1.6)].join('');
  const all = parts.join('');
  return [
    fill(P.ink, all),
    flat(P.mud, all),
    fill(P.mud, flecks, { w: 1 }),
    shine(ellipse(13.2, 11.4, 2.4, 1.4), 0.3),
  ];
}

/** The tire planter: a white-painted tire laid flat (top ring, tread band toward us), flowers in the dirt. */
function tirePlanter(): Shape[] {
  const [cx, top, bot, rx, ry] = [16, 18.8, 23.2, 13, 5];
  let tread = '';
  for (const t of [-0.866, -0.588, -0.208, 0.208, 0.588, 0.866]) {
    const x = cx + rx * t;
    const dy = ry * Math.sqrt(1 - t * t);
    tread += `M${r2(x)} ${r2(top + dy + 0.9)}V${r2(bot + dy - 1.6)}`;
  }
  return [
    fill(P.lightGray, `M${cx - rx} ${top}V${bot}A${rx} ${ry} 0 0 0 ${cx + rx} ${bot}V${top}Z`),
    line(tread, 1.5, P.gray),
    fill(P.white, ellipse(cx, top, rx, ry)),
    fill(P.darkBrown, ellipse(16, 18.4, 7.8, 2.7), { w: 1.4 }),
    line('M10 18.4V12.6M16 18.8V9M22 18.4V12.6', 1.6, P.darkGreen),
    fill(P.green, 'M15.8 17.8C13.2 17.8 11.6 16.2 11.2 14C13.6 14 15.4 15.4 15.8 17.8Z', { w: 1.2 }),
    fill(P.green, 'M16.2 17.8C18.8 17.8 20.4 16.2 20.8 14C18.4 14 16.6 15.4 16.2 17.8Z', { w: 1.2 }),
    fill(P.red, flower(9.8, 10.6, 4.2) + flower(22.2, 10.6, 4.2), { w: 1.2 }),
    fill(P.yellow, circle(9.8, 10.6, 1.4) + circle(22.2, 10.6, 1.4), { w: 1 }),
    fill(P.yellow, flower(16, 7, 4.4), { w: 1.2 }),
    fill(P.orange, circle(16, 7, 1.5), { w: 1 }),
  ];
}

/** The BMX frame tubes, stroked twice (ink, then red) so the tubes get an outline. */
const BMX_FRAME =
  'M10.8 15.4L8 21.2M11.4 12.8L19.6 13.8M10.8 15.4L16.4 21.6M16.4 21.6L19.6 13.8L20.2 11.6' +
  'M16.4 21.6L24 21.2L19.6 13.8M11.4 12.8L10.8 15.4';

/** The rocket's half-width at height y: a round nose cone, then a body that narrows a touch. */
const rocketW = (y: number): number =>
  y <= 14.4 ? 5.6 * Math.sqrt(Math.max(0, 1 - ((14.4 - y) / 11.6) ** 2)) : 5.6 - (y - 14.4) * 0.1;

export const vehicles = {
  /** Uncle's '87 Camaro: long hood, black stripe. */
  car: [
    fill(
      P.red,
      'M3 20C3 17.6 4.2 16.6 6.6 16.2L10.6 15.6L13.4 11.6C14 10.8 14.8 10.4 15.8 10.4H21.2C22.2 10.4 23 10.9 23.6 11.8L25.6 15.4L27.6 15.8C29 16.1 29.6 17.2 29.6 18.6V21.4C29.6 22.2 29 22.8 28.2 22.8H4.4C3.6 22.8 3 22.2 3 21.4Z',
    ),
    flat(P.sky, 'M14.8 12.2H18V15.2H12.6Z'),
    flat(P.sky, 'M19.2 12.2H21C21.5 12.2 21.9 12.5 22.2 12.9L23.4 15.2H19.2Z'),
    flat(P.black, rect(4, 17.8, 25, 1.6)),
    shine(rect(15, 11, 6, 0.8, 0.4), 0.5),
    fill(P.black, circle(9, 22.6, 3.4)),
    fill(P.black, circle(23.6, 22.6, 3.4)),
    flat(P.silver, circle(9, 22.6, 1.4)),
    flat(P.silver, circle(23.6, 22.6, 1.4)),
    line('M3.6 18.6H5', 1.2, P.amber),
  ],
  /** Big Bubba: a lifted blue 4x4 pickup way up on huge knobby mud tires, a light bar on the roof. */
  'lifted-truck': [
    fill(P.charcoal, rect(8, 17.6, 16, 3, 0.8)),
    fill(
      P.blue,
      'M3.8 11.8L11.4 11.4L13.8 5.8C14.1 5.2 14.6 4.8 15.3 4.8H19.4C20.1 4.8 20.6 5.3 20.6 6V11.2H28.2C28.8 11.2 29.2 11.6 29.2 12.2V17.8C29.2 18.4 28.8 18.8 28.2 18.8H4C3.4 18.8 3 18.4 3 17.8V12.6C3 12.2 3.4 11.8 3.8 11.8Z',
    ),
    flat(P.sky, poly(12.9, 10.6, 14.7, 6.4, 19.4, 6.4, 19.4, 10.6)),
    line('M20.6 11.6V18', 1.2),
    flat(P.lemon, rect(3.8, 12.6, 1.8, 1.8, 0.4)),
    shine(rect(15.4, 5.6, 3.4, 0.8, 0.4), 0.5),
    fill(P.yellow, rect(14.6, 2.6, 5.8, 2.4, 0.8), { w: 1.2 }),
    flat(P.black, knobby(8.6, 23.4, 5.4) + knobby(23.6, 23.4, 5.4)),
    flat(P.charcoal, circle(8.6, 23.4, 3.2) + circle(23.6, 23.4, 3.2)),
    flat(P.silver, circle(8.6, 23.4, 2) + circle(23.6, 23.4, 2)),
  ],
  /** A big-rig semi cab, three-quarter front view: red cab, big chrome grill and bumper, a smokestack. */
  semi: semiTruck(),
  /** A white box truck (moving / delivery), side view: little cab, big box. */
  'box-truck': [
    fill(
      P.white,
      'M3 21.2V17.6C3 16.8 3.5 16.2 4.3 16L5.8 15.6L8 11.2C8.3 10.7 8.8 10.4 9.4 10.4H13V22.4H4.2C3.5 22.4 3 21.9 3 21.2Z',
    ),
    flat(P.sky, poly(7.1, 15, 8.9, 11.4, 11, 11.4, 11, 15)),
    fill(P.white, rect(11.8, 5, 17.4, 17.4, 1.2)),
    flat(P.blue, rect(12.6, 14.4, 15.8, 2.2)),
    line('M3.6 18.6H5', 1.2, P.amber),
    ...wheels([8, 24], 22.6),
  ],
  /** The candy-paint donk: a purple '70s sedan sitting way up on giant shiny chrome rims. */
  donk: [
    fill(
      P.purple,
      'M3.8 13.2H10.8L13 9C13.3 8.5 13.8 8.2 14.4 8.2H19.8C20.4 8.2 20.9 8.5 21.2 9L23.4 13H28.4C28.9 13 29.2 13.3 29.2 13.8V18.8C29.2 19.4 28.8 19.8 28.2 19.8H4C3.4 19.8 3 19.4 3 18.8V14C3 13.6 3.4 13.2 3.8 13.2Z',
    ),
    flat(P.sky, poly(12.3, 12.6, 14, 9.4, 16.8, 9.4, 16.8, 12.6)),
    flat(P.sky, poly(17.8, 9.4, 20.2, 9.4, 21.9, 12.6, 17.8, 12.6)),
    shine(rect(4.4, 14.4, 23.8, 1, 0.5), 0.45),
    fill(P.black, circle(8.8, 23.6, 5.2) + circle(23.6, 23.6, 5.2)),
    fill(P.silver, circle(8.8, 23.6, 3.9) + circle(23.6, 23.6, 3.9), { w: 1.2 }),
    flat(P.gray, star(8.8, 23.6, 3, 1.2) + star(23.6, 23.6, 3, 1.2)),
    flat(P.white, circle(8.8, 23.6, 0.9) + circle(23.6, 23.6, 0.9)),
    shine(
      'M6.2 22.4A2.9 2.9 0 0 1 8.2 20.8L8.4 21.8A2 2 0 0 0 7.1 22.9Z' +
        'M21 22.4A2.9 2.9 0 0 1 23 20.8L23.2 21.8A2 2 0 0 0 21.9 22.9Z',
      0.9,
    ),
  ],
  /** A '79 El Camino, side view: car up front, an open pickup bed out back. Tan-gold. */
  'el-camino': [
    fill(
      P.amber,
      'M3 20.4C3 18 4.2 16.4 6.4 15.8L11 15L13.4 11.6C13.8 11.1 14.4 10.8 15 10.8H18C18.6 10.8 19 11.1 19.3 11.6L20.6 13.4H28.4C28.9 13.4 29.2 13.7 29.2 14.2V21.4C29.2 22.2 28.6 22.8 27.8 22.8H4.4C3.6 22.8 3 22.2 3 21.4Z',
    ),
    flat(P.charcoal, poly(20.4, 14.2, 28.4, 14.2, 28.4, 16.8, 21.4, 16.8)),
    line('M21 16.8H28.8', 1.3),
    flat(P.sky, poly(12.9, 14.4, 15, 11.8, 17.8, 11.8, 17.8, 14.4)),
    flat(P.brown, rect(3.8, 19, 24.6, 1.3)),
    shine(rect(14.6, 11.4, 3.2, 0.8, 0.4), 0.5),
    ...wheels([9, 24], 22.6),
    line('M3.6 18.4H5', 1.2, P.lemon),
  ],
  /** A black-and-white sheriff's cruiser, side view: red and blue light bar, gold star on the door. */
  'police-car': [
    fill(
      P.black,
      'M3 20.6C3 18.4 4.2 17.2 6.4 16.8L10.6 16L12.8 12.2C13.2 11.6 13.8 11.2 14.6 11.2H21C21.8 11.2 22.4 11.6 22.8 12.2L25 16L27.4 16.4C28.6 16.6 29.2 17.6 29.2 18.8V21.4C29.2 22.2 28.6 22.8 27.8 22.8H4.4C3.6 22.8 3 22.2 3 21.4Z',
    ),
    flat(P.white, poly(11.6, 22, 11.6, 16.4, 13.9, 12, 21.7, 12, 24.2, 16.4, 24.2, 22)),
    flat(P.sky, poly(13.2, 15.6, 14.8, 12.9, 17.6, 12.9, 17.6, 15.6)),
    flat(P.sky, poly(18.6, 12.9, 21.2, 12.9, 22.8, 15.6, 18.6, 15.6)),
    fill(P.red, rect(13.2, 7.9, 4.8, 3.3, 1)),
    fill(P.blue, rect(18, 7.9, 4.8, 3.3, 1)),
    fill(P.gold, star(17.4, 18.8, 2.3, 1.05), { w: 1 }),
    ...wheels([9, 24], 22.6),
  ],
  /** The conversion van: boxy and black, a wolf howling at a full moon painted on the side. */
  'wolf-van': [
    fill(
      P.black,
      'M3 21.4V16.8C3 16 3.5 15.4 4.3 15.2L6 14.8L8.8 8.2C9.1 7.6 9.7 7.2 10.4 7.2H27.6C28.5 7.2 29.2 7.9 29.2 8.8V21.4C29.2 22.2 28.6 22.8 27.8 22.8H4.4C3.6 22.8 3 22.2 3 21.4Z',
    ),
    flat(P.sky, poly(7.45, 14.2, 9.9, 8.4, 13, 8.4, 13, 14.2)),
    flat(P.navy, rect(14.2, 8.4, 14, 12.4, 1.6)),
    flat(P.lemon, circle(22.6, 13.4, 4.4)),
    flat(
      P.black,
      'M14.2 20.8V19.6L17 19L18 17.9L18.9 18L19.4 15.8L20.2 13.9L20.4 12L20.8 11L21.3 12.1L23.9 9.9L23.7 10.5L22.3 13.1L22.1 15L22.6 19L25.4 19.2L28.2 19.8V20.8Z',
    ),
    ...wheels([8.6, 24], 22.6),
  ],
  /** A red three-wheeler (an ATC): fat balloon tires, black seat, high bars. */
  'three-wheeler': [
    fill(P.charcoal, circle(24.6, 21.2, 4.5)),
    fill(P.charcoal, rect(12.4, 18.6, 5.6, 4.6, 1)),
    ...wheels([8.6], 22.8, 4.8, 1.9),
    ...wheels([22.8], 22.6, 5, 2),
    line('M8.6 22.8L11.6 13.8', 2),
    fill(
      P.red,
      'M3.2 21C3.2 16.6 5.6 14.8 8.6 14.8C11.6 14.8 14 16.6 14 21H13C12.6 18.4 10.8 17 8.6 17C6.4 17 4.6 18.4 4.2 21Z',
    ),
    fill(
      P.red,
      'M12.6 20.6L12.2 16.2C12.1 15 12.9 14.2 14.1 14.2H27.4C28.6 14.2 29.3 15.2 29.1 16.4L28.7 19.4L28.24 20.06A6 6 0 0 0 17.16 20.55Z',
    ),
    fill(P.black, rect(14.4, 11.6, 11.2, 2.8, 1.4)),
    line(polyline(11.6, 13.8, 10.8, 10.4, 13.6, 10.6), 2),
    fill(P.lemon, circle(9.8, 14.2, 1.4), { w: 1.2 }),
  ],
  /** A green riding lawn mower with a high-back yellow seat. */
  'riding-mower': [
    fill(P.darkGreen, rect(9.2, 22.2, 13.4, 4.4, 1.6)),
    fill(
      P.green,
      'M3.6 21.8V18.8C3.6 16.4 5.2 15 7.8 14.8L13.6 14.2C14.4 14.2 14.8 14.6 14.8 15.4V18.4H22V22.4H4.2C3.8 22.4 3.6 22.2 3.6 21.8Z',
    ),
    flat(P.yellow, rect(4.6, 17.6, 9.2, 1.2)),
    ...wheels([7.2], 24.4, 3.2, 1.3),
    ...wheels([22.6], 22.6, 5, 2),
    fill(P.green, 'M15.8 17.4C16.2 14.8 18.6 13.4 21.6 13.4H25.4C27.6 13.4 29 15 29 17.4V19H15.8Z'),
    line(polyline(13.6, 14.6, 11.8, 9.6), 1.6),
    line('M9.4 10.4L14 8.8', 2),
    fill(
      P.yellow,
      'M17.8 11.4H22.8L24 6.4C24.2 5.6 24.8 5.2 25.6 5.4C26.4 5.6 26.8 6.2 26.6 7L25.2 13.4C25.1 13.8 24.8 14 24.4 14H17.8C17.1 14 16.6 13.5 16.6 12.8V12.6C16.6 11.9 17.1 11.4 17.8 11.4Z',
    ),
  ],
  /**
   * The Lil' Rascal: a little red mobility scooter (not a moped!) with a captain's seat and armrest,
   * a steering tiller, a wire basket up front and a tall whip with an orange safety flag.
   */
  rascal: [
    line('M26.6 20V2.8', 1.3),
    fill(P.orange, poly(26.6, 2.6, 20.8, 4.6, 26.6, 6.6), { w: 1.2 }),
    fill(
      P.red,
      'M16.8 25.2V21C16.8 19.9 17.7 19.2 18.8 19.2H25.2C27.2 19.2 28.4 20.8 28.4 22.8V24C28.4 24.7 27.9 25.2 27.2 25.2Z',
    ),
    fill(P.darkGray, rect(18.6, 17.8, 2, 2)),
    fill(
      P.red,
      'M4.8 25.2V22.4C4.8 20.8 6 19.8 7.6 19.8H9.8L11.6 12.2C11.7 11.6 12.2 11.2 12.8 11.2H13.6C14.3 11.2 14.7 11.8 14.5 12.4L12.6 20.4V22.6H17.6V25.2Z',
    ),
    line('M13.2 11.4L16.4 10.8', 2.2),
    fill(P.silver, poly(3, 12.4, 10.6, 12.4, 9.8, 18.8, 3.8, 18.8)),
    line('M5.4 13.8V17.4M7.8 13.8V17.4M4.6 15.6H8.8', 1.1, P.gray),
    fill(
      P.charcoal,
      'M16.6 15.4H21V10C21 9.2 21.6 8.6 22.4 8.6H23C23.8 8.6 24.4 9.2 24.4 10V17.2C24.4 17.9 23.9 18.4 23.2 18.4H16.6C15.9 18.4 15.4 17.9 15.4 17.2V16.6C15.4 15.9 15.9 15.4 16.6 15.4Z',
    ),
    fill(P.charcoal, rect(15.6, 12.4, 6.4, 1.8, 0.9)),
    line('M17 14.2V15.4', 1.4),
    ...wheels([8.4, 23.6], 25.7, 2.7, 1.1),
  ],
  /** A white ambulance, side view: red stripe, red cross, a red light on the roof. */
  ambulance: [
    fill(P.red, rect(13.6, 3.2, 4.4, 3.2, 1)),
    fill(
      P.white,
      'M3 21.4V17.2C3 16.4 3.5 15.8 4.3 15.6L5.8 15.2L8.4 9.8C8.7 9.2 9.3 8.8 10 8.8H12.6V7.4C12.6 6.7 13.1 6.2 13.8 6.2H28C28.7 6.2 29.2 6.7 29.2 7.4V21.4C29.2 22.2 28.6 22.8 27.8 22.8H4.4C3.6 22.8 3 22.2 3 21.4Z',
    ),
    flat(P.sky, poly(7.4, 14.6, 9.4, 10, 11.8, 10, 11.8, 14.6)),
    flat(P.red, rect(4, 16.4, 24.4, 1.8)),
    flat(P.red, 'M19.9 8.2H22.1V10.3H24.2V12.5H22.1V14.6H19.9V12.5H17.8V10.3H19.9Z'),
    ...wheels([8.6, 24], 22.6),
  ],
  /** A BMX bike, side view, red frame and tall bars. */
  bike: [
    line(circle(8, 21.2, 4.6) + circle(24, 21.2, 4.6), 2.4, P.black),
    line(circle(8, 21.2, 3.1) + circle(24, 21.2, 3.1), 0.9, P.silver),
    line(
      'M8 17.6V24.8M4.9 19.4L11.1 23M4.9 23L11.1 19.4M24 17.6V24.8M20.9 19.4L27.1 23M20.9 23L27.1 19.4',
      1,
      P.gray,
    ),
    line(BMX_FRAME, 3.4),
    line(BMX_FRAME, 1.8, P.red),
    line(polyline(11.4, 12.8, 11.2, 7.4, 14, 7), 1.8),
    fill(
      P.black,
      'M17.8 10.8H22.4C23 10.8 23.2 11.4 22.8 11.8L22 12.4H18.4C17.8 12.4 17.4 12 17.4 11.4C17.4 11 17.6 10.8 17.8 10.8Z',
      { w: 1.2 },
    ),
    fill(P.silver, circle(16.4, 21.6, 1.8), { w: 1.2 }),
    line('M16.4 21.6L14.6 24.4M13.4 24.4H15.8', 1.6),
    fill(P.silver, circle(8, 21.2, 1.1) + circle(24, 21.2, 1.1), { w: 1 }),
  ],
  /** A cartoon rocket, red and white, blasting off on a flame. */
  rocket: [
    fill(P.fire, 'M12.6 23.2C12 26 14 27.8 16 29.2C18 27.8 20 26 19.4 23.2Z'),
    flat(P.flame, 'M14.2 23.6C14 25.4 15 26.4 16 27.2C17 26.4 18 25.4 17.8 23.6Z'),
    fill(P.red, 'M11 15.8L6.6 20.8V24.6L11 22.2Z'),
    fill(P.red, 'M21 15.8L25.4 20.8V24.6L21 22.2Z'),
    flat(P.white, profile(16, 2.8, 22.8, rocketW)),
    flat(P.red, profile(16, 2.8, 9, rocketW, 12)),
    line(profile(16, 2.8, 22.8, rocketW), 1.6),
    line('M11.5 9C14.4 10 17.6 10 20.5 9', 1.4),
    fill(P.sky, circle(16, 14.4, 2.6)),
    shine(ellipse(15.1, 13.5, 0.9, 0.7), 0.7),
    fill(P.red, rect(14.8, 18, 2.4, 6.6, 1.2)),
  ],
  /** A flying saucer: silver disc, glass dome, little colored lights, a pale green beam below. */
  ufo: [
    flat(P.lime, poly(11.4, 17.4, 20.6, 17.4, 27, 29.4, 5, 29.4), 0.5),
    fill(P.glass, 'M10 12.6C10 8.4 12.6 5.4 16 5.4C19.4 5.4 22 8.4 22 12.6Z'),
    shine(ellipse(13.6, 8.6, 1.2, 1.6), 0.7),
    fill(P.gray, ellipse(16, 18.4, 5, 2)),
    fill(P.silver, ellipse(16, 14.6, 13.2, 4.4)),
    flat(
      P.gray,
      'M4.2 15.6C6.4 17.4 10.8 18.2 16 18.2C21.2 18.2 25.6 17.4 27.8 15.6C25.6 16.6 21.2 17.1 16 17.1C10.8 17.1 6.4 16.6 4.2 15.6Z',
    ),
    flat(P.yellow, circle(7.6, 15, 1.2)),
    flat(P.hotPink, circle(12, 15.8, 1.2)),
    flat(P.lime, circle(16, 16, 1.2)),
    flat(P.sky, circle(20, 15.8, 1.2)),
    flat(P.yellow, circle(24.4, 15, 1.2)),
  ],
  /** A white motor yacht on a bit of blue water. */
  yacht: [
    fill(P.white, 'M13.8 6.2H21.6C22.2 6.2 22.6 6.6 22.6 7.2V9.6H13.8Z'),
    fill(P.white, 'M9.6 14L13 9.4C13.3 9 13.8 8.8 14.4 8.8H24.2C24.9 8.8 25.4 9.3 25.4 10V14Z'),
    flat(P.navy, poly(12.4, 13, 14.2, 10.4, 24.4, 10.4, 24.4, 13)),
    fill(P.white, 'M2.8 13C9 14.4 18 14.4 29.2 14L28.4 21H8.6C6 19.2 4 16.4 2.8 13Z'),
    flat(P.navy, 'M5.2 16.4C11 17.2 19 17.2 28 16.8L27.9 18C19 18.4 11 18.4 6 17.8Z'),
    fill(
      P.water,
      'M2.8 21C4.4 19.6 6.2 19.6 8 21C9.8 22.4 11.6 22.4 13.4 21C15.2 19.6 17 19.6 18.8 21C20.6 22.4 22.4 22.4 24.2 21C26 19.6 27.6 19.6 29.2 21V26.2C29.2 27.2 28.4 28 27.4 28H4.6C3.6 28 2.8 27.2 2.8 26.2Z',
    ),
    line('M7 24.4C9 25.4 11 25.4 13 24.4M18.6 24.4C20.6 25.4 22.6 25.4 24.6 24.4', 1.2, P.white),
  ],
  /** An old-timey red gas pump with a glass globe on top, hose and nozzle hung on the side. */
  'gas-pump': [
    line('M18.8 21.6C23.6 22 25.6 20.2 25.6 16.2V13.2', 2.2, P.black),
    fill(P.charcoal, rect(4.6, 26.2, 15.6, 3, 1)),
    fill(P.white, circle(12.4, 6, 3.2)),
    flat(P.red, rect(10, 5.2, 4.8, 1.6)),
    fill(P.red, rect(5.8, 8.8, 13.2, 17.6, 2)),
    fill(P.cream, rect(8, 11, 8.8, 6.4, 1)),
    line('M10 13.4H14.8M10 15.4H13.2', 1.2),
    shine(rect(7, 10, 1.2, 14.6, 0.6), 0.35),
    fill(P.charcoal, 'M23.6 8.6H26.4C27 8.6 27.4 9 27.4 9.6V13.6H23.6Z'),
    line('M23.6 9.8L20.4 11.2', 1.8, P.charcoal),
  ],
  /** A red air horn: a canister with a flared trumpet (the honk button). */
  'air-horn': [
    fill(P.red, 'M11 8.8L19.2 8C21.6 7.8 23.2 6.4 24.2 4.8V16.8C23.2 15.2 21.6 13.8 19.2 13.6L11 12.8Z'),
    fill(P.darkRed, ellipse(24.2, 10.8, 1.6, 6)),
    fill(P.red, rect(4.4, 14.2, 9.6, 13.4, 2)),
    flat(P.white, rect(5.2, 18.6, 8, 3.4)),
    fill(P.silver, 'M5.2 14.4C5.2 12.4 6.8 11.4 9.2 11.4C11.6 11.4 13.2 12.4 13.2 14.4Z'),
    fill(P.charcoal, rect(7.2, 7.8, 4, 4, 0.8)),
    line('M27 7.2C28.2 9.2 28.2 12.4 27 14.4', 1.6),
  ],
  /** A black-and-white checkered racing flag on a pole, waving. */
  'checkered-flag': checkeredFlag(),
  /** A red rotating police beacon with glow lines. */
  siren: [
    line('M3.4 16H6.4M7.1 7.1L9.2 9.2M16 3V6M24.9 7.1L22.8 9.2M28.6 16H25.6', 1.8, P.amber),
    fill(P.charcoal, rect(6.4, 22.4, 19.2, 5.6, 1.6)),
    fill(P.red, 'M8.8 22.8V16C8.8 11.4 12 8.2 16 8.2C20 8.2 23.2 11.4 23.2 16V22.8Z'),
    flat(P.blush, ellipse(16, 16.6, 2.6, 4.2)),
    shine(rect(11.2, 12, 1.8, 8, 0.9), 0.5),
  ],
  /** A big splat of brown mud with drips and flecks (the mud bog, the car-wash hustle). */
  'mud-splat': mudSplat(),
  /** An old tire painted white, laid flat, with red and yellow flowers growing out of it. */
  'tire-planter': tirePlanter(),
} satisfies Record<string, IconDef>;
